import { useState } from 'react';
import type { CashFlowEntry } from '../page';

interface CashFlowTableProps {
  entries: CashFlowEntry[];
  loading: boolean;
  search: string;
  filterType: string;
  filterClient: string;
  filterProject: string;
  dateFrom: string;
  dateTo: string;
  clients: { id: string; name: string }[];
  projects: { id: string; name: string }[];
  totalCredits: number;
  totalDebits: number;
  balance: number;
  onSearchChange: (v: string) => void;
  onFilterTypeChange: (v: string) => void;
  onFilterClientChange: (v: string) => void;
  onFilterProjectChange: (v: string) => void;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
  onDelete: (id: string) => void;
  onOpenCredit: () => void;
}

const ORIGIN_LABELS: Record<string, string> = {
  manual: 'Manual',
  project: 'Projeto',
  task: 'Atividade',
  advance: 'Adiantamento',
  refund: 'Reembolso',
  expense: 'Despesa',
  salary: 'Salário',
  tax: 'Imposto',
  tool: 'Ferramenta',
  other: 'Outro',
  auto: 'Automático',
};

const PAYMENT_LABELS: Record<string, string> = {
  pix: 'PIX',
  credit_card: 'Cartão Crédito',
  debit_card: 'Cartão Débito',
  boleto: 'Boleto',
  bank_transfer: 'Transferência',
  cash: 'Dinheiro',
};

export default function CashFlowTable({
  entries, loading, search, filterType, filterClient, filterProject,
  dateFrom, dateTo, clients, projects, totalCredits, totalDebits, balance,
  onSearchChange, onFilterTypeChange, onFilterClientChange, onFilterProjectChange,
  onDateFromChange, onDateToChange, onDelete, onOpenCredit,
}: CashFlowTableProps) {
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const fmtDate = (d: string) => new Date(d + 'T12:00:00').toLocaleDateString('pt-BR');

  return (
    <div>
      {/* Filtros */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por descrição, cliente ou projeto..."
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300"
          />
        </div>
        <select value={filterType} onChange={e => onFilterTypeChange(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 cursor-pointer">
          <option value="">Créditos e Débitos</option>
          <option value="credit">Somente Créditos</option>
          <option value="debit">Somente Débitos</option>
        </select>
        <select value={filterClient} onChange={e => onFilterClientChange(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 cursor-pointer">
          <option value="">Todos os clientes</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filterProject} onChange={e => onFilterProjectChange(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 cursor-pointer">
          <option value="">Todos os projetos</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={e => onDateFromChange(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300"
          title="Data inicial"
        />
        <input
          type="date"
          value={dateTo}
          onChange={e => onDateToChange(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300"
          title="Data final"
        />
      </div>

      {/* Resumo do período filtrado */}
      {(filterType || filterClient || filterProject || dateFrom || dateTo || search) && (
        <div className="flex items-center gap-4 mb-4 px-4 py-3 bg-slate-50 rounded-lg border border-slate-200 text-sm">
          <span className="text-slate-500">Período filtrado:</span>
          <span className="text-emerald-600 font-medium">+{fmt(totalCredits)}</span>
          <span className="text-red-500 font-medium">-{fmt(totalDebits)}</span>
          <span className={`font-semibold ${balance >= 0 ? 'text-slate-800' : 'text-red-600'}`}>
            Saldo: {fmt(balance)}
          </span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-emerald-500 text-2xl"></i>
        </div>
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <i className="ri-safe-line text-5xl mb-4"></i>
          <p className="text-sm font-medium text-slate-500">Nenhum lançamento encontrado</p>
          <p className="text-xs mt-1">Registre créditos e débitos para começar</p>
          <button onClick={onOpenCredit} className="mt-4 text-emerald-500 text-sm hover:underline cursor-pointer">
            Lançar primeiro crédito
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Data</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Tipo</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Descrição</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Origem</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Cliente</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Projeto</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Pagamento</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Responsável</th>
                  <th className="text-right px-4 py-3 text-slate-500 font-medium">Valor</th>
                  <th className="text-right px-4 py-3 text-slate-500 font-medium w-12"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map(entry => (
                  <tr key={entry.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{fmtDate(entry.reference_date)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        entry.flow_type === 'credit'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-red-50 text-red-600'
                      }`}>
                        <i className={entry.flow_type === 'credit' ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>
                        {entry.flow_type === 'credit' ? 'Crédito' : 'Débito'}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-[200px]">
                      <p className="text-slate-700 font-medium truncate">{entry.description || '—'}</p>
                      {entry._task_title && (
                        <p className="text-slate-400 text-xs truncate mt-0.5">
                          <i className="ri-task-line mr-1"></i>{entry._task_title}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">
                        {ORIGIN_LABELS[entry.origin] || entry.origin}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{entry._client_name}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{entry._project_name}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {entry.payment_method ? PAYMENT_LABELS[entry.payment_method] || entry.payment_method : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{entry._created_by_name}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-semibold text-sm ${entry.flow_type === 'credit' ? 'text-emerald-600' : 'text-red-500'}`}>
                        {entry.flow_type === 'credit' ? '+' : '-'}{Number(entry.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {entry.origin === 'manual' && (
                        <button
                          onClick={() => setDeleteConfirm(entry.id)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer ml-auto"
                          title="Excluir"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Totais */}
              <tfoot>
                <tr className="border-t-2 border-slate-200 bg-slate-50">
                  <td colSpan={8} className="px-4 py-3 text-slate-600 text-sm font-medium">
                    Total ({entries.length} lançamentos)
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-emerald-600 text-xs">+{fmt(totalCredits)}</span>
                      <span className="text-red-500 text-xs">-{fmt(totalDebits)}</span>
                      <span className={`font-bold text-sm ${balance >= 0 ? 'text-slate-800' : 'text-red-600'}`}>
                        {fmt(balance)}
                      </span>
                    </div>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm">
            <h3 className="text-slate-800 font-semibold mb-2">Excluir lançamento?</h3>
            <p className="text-slate-500 text-sm mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
              <button
                onClick={() => { onDelete(deleteConfirm); setDeleteConfirm(null); }}
                className="px-4 py-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}